import React, { ChangeEvent } from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';
import axios from 'axios';

import { createReactRenderer } from '../helpers/react-renderer';
import { getHostname } from '../label/utils';
import { getDateTimeFormat } from '../pages/utils/time';
import { sanitizeText } from 'pages/utils/sanitize';
import { hasFeature } from 'root/api/admin/features';

import {
  StyledItemDescription,
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
} from './GiftReceiptModal.style';
import NumberInputStepper, { NumberInputStepperProps } from './NumberInputStepper';
import { getCustomLogoFromSession, setCustomLogoToSession } from './utils';

export const REST_CALL_ERROR_MESSAGE = "The sale information can't be retrieved. Try again";
// eslint-disable-next-line unused-imports/no-unused-vars-ts
let originalSaleData: any;
let shopCustomLogoUrl: string;

type Line = {
  itemDescription: string;
  selectedPrintQuantity: number;
  unitQuantity: string;
  saleLineID: string;
  selected: boolean;
};

type SaleLineRowProps = Line & {
  onCheckClick: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Pick<NumberInputStepperProps, 'onStepperInputChange'>;

const SaleLineRow: React.FC<SaleLineRowProps> = ({
  saleLineID,
  itemDescription,
  unitQuantity,
  selected,
  selectedPrintQuantity,
  onCheckClick,
  onStepperInputChange,
}) => {
  const parsedUnitQuantity = parseInt(unitQuantity, 10);
  return (
    <>
      <Flex alignItems="center" data-test="sale-line">
        <Box width="15%" flex="0 0 auto" pl={4} py={2}>
          <Checkbox
            id={`sale-line-checkbox-${saleLineID}`}
            name="sale-line"
            data-test="sale-selector"
            checked={selected}
            onChange={onCheckClick}
          />
        </Box>
        <Box width="50%" px={4} py={2} data-test="gr-item-description">
          <StyledItemDescription>
            <var>{sanitizeText(itemDescription)}</var>
          </StyledItemDescription>
        </Box>
        <Box width="35%" px={1} py={1}>
          <Flex justifyContent="center" data-test="gr-unit-quantity">
            {parsedUnitQuantity > 1 ? (
              <NumberInputStepper
                value={selectedPrintQuantity}
                limit={[1, parsedUnitQuantity]}
                disabled={!selected}
                onStepperInputChange={onStepperInputChange}
              />
            ) : (
              <var>{parsedUnitQuantity}</var>
            )}
          </Flex>
        </Box>
      </Flex>
      <Divider />
    </>
  );
};

const saleItemTransformFunctionV2 = ({
  unitQuantity,
  saleLineID,
  Item,
  Note,
}: {
  unitQuantity: string;
  saleLineID: string;
  Item: { description: string };
  Note: { note: string };
}) => ({
  unitQuantity,
  itemDescription: Item ? Item.description : Note?.note,
  saleLineID,
  selectedPrintQuantity: 1,
  selected: false,
});

const saleItemTransformFunction = ({
  unitQuantity,
  saleLineID,
  Item,
}: {
  unitQuantity: string;
  saleLineID: string;
  Item: { description: string };
}) => ({
  unitQuantity,
  itemDescription: Item?.description,
  saleLineID,
  selectedPrintQuantity: 1,
  selected: false,
});

const getSelectedSaleLines = (saleLines: Line[]) =>
  saleLines
    ?.filter((saleLine) => saleLine.selected)
    ?.map(({ selectedPrintQuantity, itemDescription, saleLineID }) => ({
      description: itemDescription,
      selectedQuantity: selectedPrintQuantity,
      saleLineID,
    }));

const getSale = async (saleId: string | undefined, onError: () => void) => {
  const accountId = window.merchantos.session.getSystemCustomerID();
  const shopId = window.merchantos.session.sessData?.Shop?.shopID;
  let customLogo = getCustomLogoFromSession(shopId) as string | undefined | null;
  const isTemplateEditAllowed = window.lsCloud.Modules.hasModule('templates_edit');
  const shouldFetchLogo = isTemplateEditAllowed && !customLogo;

  try {
    const [{ value: saleData }, { value: saleReceiptTemplateData }] = (await Promise.allSettled([
      axios.get(`API/Account/${accountId}/DisplayTemplate/Sale/${saleId}.json?load_relations=all`),
      shouldFetchLogo
        ? axios.get(
            `API/Account/${accountId}/DisplayTemplate/Sale/${saleId}.html?template=SaleReceipt&gift_receipt=1&page_width=auto&page_height=2000mm`
          )
        : null,
    ])) as any;

    const {
      data: { Sale },
    } = saleData;
    originalSaleData = Sale;

    const {
      SaleLines: { SaleLine },
    } = Sale;

    if (shouldFetchLogo) {
      const parsedDom = new DOMParser().parseFromString(saleReceiptTemplateData.data, 'text/html');
      customLogo = parsedDom.body.querySelector('img.logo')?.getAttribute('src');

      if (customLogo && shopId) setCustomLogoToSession(shopId, customLogo);
    }

    return { saleLines: SaleLine, customLogo };
  } catch (error) {
    console.error('Unable to fetch sale details for itemized gift receipt', error);
    onError();
    return {};
  }
};

export const GiftReceiptModal: React.FC<{
  saleId: string;
  url: string;
  el?: HTMLElement;
}> = ({ saleId, url, el }) => {
  const [modalOpen, setModalOpen] = React.useState(true);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [saleLinesData, setSaleLinesData] = React.useState<Line[] | undefined>(undefined);
  const [hasError, setHasError] = React.useState(false);
  const [isPrinting, setIsPrinting] = React.useState(false);

  React.useEffect(() => {
    const fetchItem = async () => {
      const onError = () => {
        setHasError(true);
      };
      setIsLoadingData(true);

      const { saleLines, customLogo } = await getSale(saleId, onError);

      if (saleLines) {
        const giftReceiptFixNonInventoryItems = await hasFeature('GiftReceiptFixNonInventoryItems');
        // We're filtering out the item fees to not show them on the gift receipt modal
        let transformedData = [];
        if (giftReceiptFixNonInventoryItems) {
          transformedData = Array.isArray(saleLines)
            ? saleLines
                ?.filter(
                  (saleLine) => saleLine.lineType !== 'item_fee' && (saleLine.Item || saleLine.Note)
                )
                .map(saleItemTransformFunctionV2)
            : [saleLines]
                ?.filter(
                  (saleLine) => saleLine.lineType !== 'item_fee' && (saleLine.Item || saleLine.Note)
                )
                .map(saleItemTransformFunctionV2);
        } else {
          transformedData = Array.isArray(saleLines)
            ? saleLines
                ?.filter((saleLine) => saleLine.lineType !== 'item_fee')
                .map(saleItemTransformFunction)
            : [saleLines]
                ?.filter((saleLine) => saleLine.lineType !== 'item_fee')
                .map(saleItemTransformFunction);
        }
        setSaleLinesData(transformedData);
        setHasError(false);
      }

      if (customLogo) shopCustomLogoUrl = customLogo;

      setIsLoadingData(false);
    };

    fetchItem();
  }, [saleId]);

  const addRemoveItem = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>, saleLineID: string) => {
      const { checked } = e.target;
      setSaleLinesData((prev) =>
        prev?.map((item) => {
          const newSelection = saleLineID === item?.saleLineID ? checked : item?.selected;
          return { ...item, selected: newSelection };
        })
      );
    },
    []
  );

  const handleAddRemoveAll = React.useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setSaleLinesData((prev) => prev?.map((item) => ({ ...item, selected: checked })));
  }, []);

  const handlePrintClick = async () => {
    setIsPrinting(true);

    try {
      const dateTimeFormat = await getDateTimeFormat();
      // The local next server us running at http://receipt-editor.localhost:3021/api/giftreceipt
      const res = await fetch(`https://receipt-editor.${getHostname()}/api/giftreceipt`, {
        method: 'POST',
        body: JSON.stringify({
          sale: originalSaleData,
          selectedSaleLines: getSelectedSaleLines(saleLinesData as Line[]),
          dateFormat: dateTimeFormat,
          language: window.Localize?.getLanguage() || 'en',
          ...(shopCustomLogoUrl ? { shopCustomLogoUrl } : null),
        }),
      });

      const srcdoc = await res.text();
      setModalOpen(false);

      window.merchantos.print.printUrl({ srcdoc, url, el, pageType: 'receipt' });
    } catch (err) {
      console.error('Itemized gift receipt failed to print', err);
      setHasError(true);
      setIsPrinting(false);
    }
  };

  const handleInputStepperChange = (val: number, saleLineID: string) =>
    setSaleLinesData((prev) =>
      prev?.map((item) => ({
        ...item,
        selectedPrintQuantity: item.saleLineID === saleLineID ? val : item.selectedPrintQuantity,
      }))
    );

  const isAllSaleItemSelected = !!saleLinesData?.every((item) => item.selected);

  return (
    <div>
      <StyledModal aside isOpen={modalOpen} onRequestClose={() => setModalOpen(false)}>
        <ModalHeader>Gift receipt</ModalHeader>
        <Box style={{ position: 'sticky' }}>
          {hasError && (
            <Box p={4}>
              <Alert type="danger" noCloseBtn data-test="api-error-alert">
                {REST_CALL_ERROR_MESSAGE}
              </Alert>
            </Box>
          )}
        </Box>
        <Flex>
          <Box width="15%" p={4} minWidth="60px">
            <Checkbox
              id="select-all-checkbox"
              data-test="select-all-checkbox"
              name="feature"
              indeterminate={!!saleLinesData?.some((item) => item.selected)}
              checked={isAllSaleItemSelected}
              onChange={handleAddRemoveAll}
              disabled={isLoadingData || hasError || !saleLinesData}
            />
          </Box>
          <Box width="50%" p={4} flex="0 0 auto">
            <Text fontWeight="bold">Item</Text>
          </Box>
          <Box width="35%" p={4} textAlign="center">
            <Text fontWeight="bold">Quantity</Text>
          </Box>
        </Flex>
        <Divider />
        <StyledModalBody>
          {isLoadingData ? (
            Array.from(Array(3).keys()).map((index) => (
              <div key={index}>
                <Flex>
                  <Box width="15%" p={4}>
                    <Bone />
                  </Box>
                  <Box width="50%" p={4}>
                    <Bone />
                  </Box>
                  <Box width="35%" p={4}>
                    <Bone />
                  </Box>
                </Flex>
                <Divider />
              </div>
            ))
          ) : (
            <Flex flexDirection="column">
              {saleLinesData &&
                saleLinesData.map(({ saleLineID, ...rest }) => (
                  <SaleLineRow
                    {...rest}
                    saleLineID={saleLineID}
                    onCheckClick={(e) => {
                      return addRemoveItem(e, saleLineID);
                    }}
                    onStepperInputChange={(val) => {
                      return handleInputStepperChange(val, saleLineID);
                    }}
                    key={saleLineID}
                  />
                ))}
            </Flex>
          )}
        </StyledModalBody>
        <StyledModalFooter>
          <Flex justifyContent="flex-end" alignItems="center">
            <Button
              onClick={() => setModalOpen(false)}
              mr={2}
              data-test="cancel-print-gift-receipt-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePrintClick}
              loading={isPrinting}
              disabled={
                (saleLinesData ? saleLinesData?.every((saleLine) => !saleLine.selected) : true) ||
                isLoadingData ||
                isPrinting
              }
              variant="secondary"
              fill
              data-test="print-gift-receipt-btn"
            >
              Print
            </Button>
          </Flex>
        </StyledModalFooter>
      </StyledModal>
    </div>
  );
};

export const openGiftReceiptModal = ({
  saleId,
  url,
  el,
}: {
  saleId: string;
  url: string;
  el?: HTMLElement;
}) => {
  const RenderedComponent = createReactRenderer({
    component: GiftReceiptModal,
    selector: '[data-react-root="ReactModal"]',
  });
  RenderedComponent.mount({ props: { saleId, url, el } });
};
